import {
  ActuatorModalityType,
  Slot,
  SlotStatus,
  ThermoActuatorType,
  DeviceDTO,
  ThermoRoomObjectDTO,
  SystemControlType,
} from '@/../client-generator/generated-clients/plants';
import {
  SlotRuleServiceType,
  WorkflowCallbackFunction,
} from './slots-assign-rules-interfaces';
import BaseSlotRuleService from './slots-assign-rules-service';

export default class AssignThermoHeatingActuatorSlotRuleService extends BaseSlotRuleService {
  declare roomObject: ThermoRoomObjectDTO;

  constructor(
    roomObject: ThermoRoomObjectDTO,
    workflowCallback: WorkflowCallbackFunction
  ) {
    super(
      roomObject,
      -1,
      ThermoActuatorType.HeatingValve,
      roomObject.actuatorModalityType ?? ActuatorModalityType.None,
      workflowCallback
    );
    this.slotRuleServiceType = SlotRuleServiceType.THERMO_HEATING_ACTUATORS;

    this._noOfAdjacent = this.getNoOfAdjacentSlots(
      roomObject.heatingSystemControlType ?? SystemControlType.None
    );
  }

  addOrUpdateSlot(
    slotViewModel: Slot,
    thermoActuatorType = ThermoActuatorType.None
  ) {
    if (slotViewModel != null) {
      const tmpActuatorType = this.getCurrentThermoActuatorType(
        slotViewModel.thermoActuatorTypes,
        this.roomObject.id
      );
      if (tmpActuatorType == ThermoActuatorType.HeatingAndCoolingValve) {
        super.addOrUpdateSlot(slotViewModel, ThermoActuatorType.CoolingValve);
      } else {
        super.addOrUpdateSlot(
          slotViewModel,
          tmpActuatorType == ThermoActuatorType.CoolingValve
            ? ThermoActuatorType.HeatingAndCoolingValve
            : ThermoActuatorType.HeatingValve
        );
      }
    }
  }

  isSameSystemControlType() {
    return !(
      this.roomObject.heatingSystemControlType != SystemControlType.None &&
      this.roomObject.coolingSystemControlType != SystemControlType.None &&
      this.roomObject.heatingSystemControlType !=
        this.roomObject.coolingSystemControlType
    );
  }

  onSlotMessage(device: DeviceDTO, slot: Slot) {
    if (this.roomObject != null) {
      const tmpActuatorType = this.getCurrentThermoActuatorType(
        slot.thermoActuatorTypes,
        this.roomObject.id
      );
      if (
        (slot.status == SlotStatus.MarkedForLinking ||
          slot.status == SlotStatus.MarkedAdjacent ||
          this.isSlotLinkedWithCurrentRoomObject(slot)) &&
        this.roomObject.actuatorModalityType == ActuatorModalityType.Mixed &&
        this.isSameSystemControlType() &&
        (this.roomObject.heatingSystemControlType ==
          SystemControlType.OneOnOffValve ||
          this.roomObject.heatingSystemControlType ==
            SystemControlType.OneOpenCloseValve ||
          this.roomObject.heatingSystemControlType ==
            SystemControlType.OneProportionalValve) &&
        ((tmpActuatorType == ThermoActuatorType.HeatingAndCoolingValve &&
          slot.status == SlotStatus.MarkedForLinking) ||
          tmpActuatorType == ThermoActuatorType.CoolingValve ||
          (tmpActuatorType == ThermoActuatorType.HeatingAndCoolingValve &&
            slot.status == SlotStatus.MarkedAdjacent))
      ) {
        this.markOrUnmarkSlot(
          this.roomObject,
          slot,
          device,
          SlotStatus.MarkedForLinking,
          SlotStatus.MarkedForLinking
        );
      } else if (
        (slot.status == SlotStatus.NotConfigured &&
          (slot.linkedObjects == null ||
            !slot.linkedObjects.length ||
            (this.isSlotLinkedWithCurrentRoomObject(slot) &&
              slot.linkedObjects.length == this.OneElement))) ||
        ((tmpActuatorType == ThermoActuatorType.HeatingValve ||
          tmpActuatorType == ThermoActuatorType.HeatingAndCoolingValve) &&
          (this.isSlotLinkedWithCurrentRoomObject(slot) ||
            slot.status == SlotStatus.MarkedForLinking ||
            slot.status == SlotStatus.MarkedAdjacent))
      ) {
        super.onSlotMessage(device, slot);
      }
    }
  }

  isSlotCompatibleForMarking(slot: Slot) {
    const tmpActuatorType = this.getCurrentThermoActuatorType(
      slot.thermoActuatorTypes,
      this.roomObject.id
    );
    return (
      slot != null &&
      this.roomObject != null &&
      this.roomObject.heatingSystemControlType ==
        this.roomObject.coolingSystemControlType &&
      tmpActuatorType == ThermoActuatorType.CoolingValve &&
      this.roomObject.actuatorModalityType == ActuatorModalityType.Mixed &&
      (this.roomObject.heatingSystemControlType ==
        SystemControlType.OneOnOffValve ||
        this.roomObject.heatingSystemControlType ==
          SystemControlType.OneOpenCloseValve ||
        this.roomObject.heatingSystemControlType ==
          SystemControlType.OneProportionalValve)
    );
  }

  isSlotCompatibleForUnmarking(slot: Slot) {
    const tmpActuatorType = this.getCurrentThermoActuatorType(
      slot.thermoActuatorTypes,
      this.roomObject.id
    );
    return (
      slot != null &&
      this.roomObject != null &&
      (tmpActuatorType == ThermoActuatorType.HeatingValve ||
        (tmpActuatorType == ThermoActuatorType.HeatingAndCoolingValve &&
          this.roomObject.actuatorModalityType == ActuatorModalityType.Mixed))
    );
  }

  updateForSlotChanging(slot: Slot) {
    if (slot != null && this.roomObject != null) {
      if (!slot.thermoActuatorTypes) slot.thermoActuatorTypes = {};
      slot.thermoActuatorTypes[this.roomObject.id ?? ''] =
        ThermoActuatorType.CoolingValve;
    }
  }

  isLimitationReached(slotsToBeMarked: Slot[] | null) {
    if (this.roomObject?.devices != null && slotsToBeMarked != null) {
      var noOfSelectedValves = this.roomObject.devices
        .flatMap((d) => d.slots)
        .filter((s) =>
          [
            ThermoActuatorType.HeatingValve,
            ThermoActuatorType.CoolingValve,
            ThermoActuatorType.HeatingAndCoolingValve,
          ].includes(
            this.getCurrentThermoActuatorType(
              s?.thermoActuatorTypes,
              this.roomObject.id
            )
          )
        ).length;

      var noOfSlotsToBeAddedInParam = slotsToBeMarked.filter(
        (s) =>
          this.getCurrentThermoActuatorType(
            s.thermoActuatorTypes,
            this.roomObject.id
          ) != ThermoActuatorType.CoolingValve
      ).length;

      if (
        noOfSelectedValves + noOfSlotsToBeAddedInParam <=
        this.MaximumNoOfThermostatSlots
      ) {
        return false;
      }
      return true;
    }

    return false;
  }
}
